import { useState, useEffect } from 'react'
import {
  Outlet,
  useNavigate,
  useLocation,
  useOutletContext,
} from 'react-router'
import clsx from 'clsx'
import { GoChevronRight, GoChevronLeft } from 'react-icons/go'
import { MdArrowOutward } from 'react-icons/md'

import { useScreenContext } from '../../context/GlobalContext'
import { CurrentUser } from '../../services/schema/User'
import Filler from '../../components/utils/Filler'

const accountRoutes = ['account', 'information', 'password', 'deactivate']
const accessibilityRoutes = ['accessibility', 'display']

const Settings: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { email, username } = useOutletContext<CurrentUser>()
  const { innerWidth } = useScreenContext()

  const pathnameParts = location.pathname.split('/')
  const windowRoute = pathnameParts[2]

  const [activeWindowRoute, setActiveWindowRoute] = useState(windowRoute)

  useEffect(() => {
    if (activeWindowRoute !== windowRoute) {
      if (accountRoutes.includes(windowRoute)) {
        setActiveWindowRoute('account')
        return
      } else if (accessibilityRoutes.includes(windowRoute)) {
        setActiveWindowRoute('accessibility')
        return
      }

      setActiveWindowRoute(windowRoute)
    }
  }, [windowRoute])

  return (
    <>
      <div
        className={clsx(
          'settings-main border-theme bgd-theme w-full max-w-[400px] grow border-r border-l',
          {
            flex: innerWidth <= 1029 && !activeWindowRoute,
            hidden: innerWidth <= 1029 && activeWindowRoute,
          },
        )}
      >
        <div className="flex h-dvh w-full flex-col">
          <div className="flex h-[50px] items-center pl-[10px] text-xl font-semibold">
            <button
              type="button"
              className="hover-main mr-[15px] flex items-center justify-center rounded-full p-[10px]"
              onClick={() => {
                navigate('/home', { replace: true })
              }}
            >
              <GoChevronLeft />
            </button>
            <h1>Settings</h1>
          </div>
          <Filler />
          <div className="flex w-full flex-col pt-[15px] text-sm">
            {/* Account */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'account',
                  'bgd-gray text-white': activeWindowRoute === 'account',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'account') return
                navigate('account')
              }}
            >
              <h1>Your account</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'account',
                  })}
                ></div>
              </div>
            </button>
            {/* Monetization */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'monetization',
                  'bgd-gray text-white': activeWindowRoute === 'monetization',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'monetization') return
                navigate('monetization')
              }}
            >
              <h1>Monetization</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'monetization',
                  })}
                ></div>
              </div>
            </button>
            {/* Premium */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'premium',
                  'bgd-gray text-white': activeWindowRoute === 'premium',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'premium') return
                navigate('premium')
              }}
            >
              <h1>Premium</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'premium',
                  })}
                ></div>
              </div>
            </button>
            {/* Subscriptions */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'subscriptions',
                  'bgd-gray text-white': activeWindowRoute === 'subscriptions',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'subscriptions') return
                navigate('subscriptions')
              }}
            >
              <h1>Creator subscriptions</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'subscriptions',
                  })}
                ></div>
              </div>
            </button>
            {/* Accessibility */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'accessibility',
                  'bgd-gray text-white': activeWindowRoute === 'accessibility',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'accessibility') return
                navigate('accessibility')
              }}
            >
              <h1>Accessibility, display and languages</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'accessibility',
                  })}
                ></div>
              </div>
            </button>
            {/* Security */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'security',
                  'bgd-gray text-white': activeWindowRoute === 'security',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'security') return
                navigate('security')
              }}
            >
              <h1>Security and account access</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'security',
                  })}
                ></div>
              </div>
            </button>
            {/* Privacy */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'privacy',
                  'bgd-gray text-white': activeWindowRoute === 'privacy',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'privacy') return
                navigate('privacy')
              }}
            >
              <h1>Privacy and safety</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'privacy',
                  })}
                ></div>
              </div>
            </button>
            {/* Notifications */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'notifications',
                  'bgd-gray text-white': activeWindowRoute === 'notifications',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'notifications') return
                navigate('notifications')
              }}
            >
              <h1>Notifications</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'notifications',
                  })}
                ></div>
              </div>
            </button>
            {/* Resources */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'resources',
                  'bgd-gray text-white': activeWindowRoute === 'resources',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'resources') return
                navigate('resources')
              }}
            >
              <h1>Additional resources</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <GoChevronRight />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'resources',
                  })}
                ></div>
              </div>
            </button>
            {/* Help */}
            <button
              type="button"
              className={clsx(
                'flex h-[50px] w-full cursor-pointer items-center justify-between pl-[15px]',
                {
                  'hover-main': activeWindowRoute !== 'help',
                  'bgd-gray text-white': activeWindowRoute === 'help',
                },
              )}
              onClick={() => {
                if (activeWindowRoute === 'help') return
                navigate('help')
              }}
            >
              <h1>Help center</h1>
              <div className="text-sub flex h-full w-auto items-center text-2xl">
                <MdArrowOutward />
                <div
                  className={clsx('ml-[8px] h-full w-[3px]', {
                    'bgd-blue': activeWindowRoute === 'help',
                  })}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>
      <div
        className={clsx(
          'border-theme bgd-theme flex h-dvh max-w-[550px] items-center justify-center border-r border-l',
          {
            hidden: innerWidth <= 1029 && !activeWindowRoute,
            flex: innerWidth <= 1029 && activeWindowRoute,
          },
        )}
      >
        {activeWindowRoute && (
          <Outlet context={{ email: email, username: username }} />
        )}
        {!activeWindowRoute && (
          <div className="flex w-full flex-col justify-center">
            <Filler />
            <div className="text-sub flex justify-center">
              Selected setting option tab will show here..
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Settings
